import { FC, useState, useEffect } from "react";
import { Spin, notification } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { getAuthToken } from "@/utils/auth";
import { getConnector } from "@/shared/api";
import { flattenConnectorResponse } from "@/utils/masterDataSync";
import DateRangeFilter, { DateFilterValue } from "@/pages/integrationStudio/components/DateRangeFilter";
import { useDashboardFiltersStore } from "@/store/dashboardFilters";
import { useTransactionsStore } from "@/store/transactions";
import { useTernaryDarkMode } from "usehooks-ts";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useNumericTabPersistence } from "@/hooks/useTabPersistence";
// @ts-ignore
import IntegrationUniverse from "./IntegrationUniverse";
// @ts-ignore
import DataInvestigator from "./DataInvestigator";
// @ts-ignore
import DataReplayEnhanced from "./DataReplayEnhanced";
// @ts-ignore
import PayloadRenderer from "./PayloadRenderer";
import ConfigEditor from "./ConfigEditor";
import RecoveryDesk from "./RecoveryDesk";
import "./studio.less";

async function integrationLoadError(error: unknown): Promise<string> {
  const response = (error as { response?: Response })?.response;
  if (response) {
    try {
      const body = await response.clone().json();
      const message = body?.message;
      if (typeof message === "string" && message.trim()) return message;
      if (Array.isArray(message) && message.length) return message.map(String).join(", ");
    } catch {
      // Response body is not JSON.
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return "This integration could not be loaded.";
}

const STUDIO_TABS = ['recovery', 'universe', 'investigator', 'replay', 'renderer', 'config-editor'] as const;
const STUDIO_LABELS = ['Recovery', 'Universe', 'Investigator', 'Replay', 'Renderer', 'Config'];

// Define the Config interface inline
interface Config {
  _id: string;
  name: string;
  connectorId: string;
  integrationId: string;
  source: {
    type: string;
    url: string;
    requestMethod: string;
    settings: {
      endpointPollInterval: number;
    };
    [key: string]: any;
  };
  destination: {
    type: string;
    url: string;
    requestMethod: string;
    settings: {
      endpointPollInterval: number;
    };
    [key: string]: any;
  };
  enrichment?: any;
  dataMapping?: any[];
  status?: string; // Add status field from connector
  [key: string]: any;
}

const IntegrationStudioPage: FC = () => {
  const { isDarkMode } = useTernaryDarkMode({ localStorageKey: "theme" });
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useNumericTabPersistence(0, [...STUDIO_TABS]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [dateFilter, setDateFilter] = useState<DateFilterValue>({
    preset: "Past 1 Day",
    startDate: null,
    endDate: null,
    displayText: "Past 1 Day"
  });

  // Use the dashboard filters store to share filter state
  const filterDates = useDashboardFiltersStore(state => state.filterDates);
  const setFilterDates = useDashboardFiltersStore(state => state.setFilterDates);
  const onPeriodSelected = useDashboardFiltersStore(state => state.onPeriodSelected);

  // Use the transactions store to share transaction state
  const transactions = useTransactionsStore(state => state.transactions);
  const fetchTransactions = useTransactionsStore(state => state.fetchTransactions);
  const runError = useTransactionsStore(state => state.error);

  useEffect(() => {
    if (!id) return;
    
    const fetchConfig = async () => {
      try {
        if (!config || (config._id !== id && config.connectorId !== id)) setLoading(true);
        setError(null);
        const token = getAuthToken();
        if (!token) {
          throw new Error("No authentication token found");
        }
        
        const raw = await getConnector(id, token, true);
        const connector = flattenConnectorResponse(raw);
        const connectorId = connector?._id || connector?.id;

        if (!connector || !connectorId) {
          throw new Error("Connector not found");
        }

        const configData = {
          ...connector,
          _id: connectorId,
          integrationId: connectorId,
          connectorId,
          source: connector.source || {},
          destination: connector.destination || {},
          dataMapping: connector.dataMapping || [],
          enrichment: connector.enrichment || [],
        };
        
        console.log("Built config from connector:", configData);
        console.log("Config source:", configData.source);
        console.log("Config destination:", configData.destination);
        console.log("Config dataMapping:", configData.dataMapping);
        setConfig(configData);
      } catch (error) {
        console.error("Error fetching integration config:", error);
        const description = await integrationLoadError(error);
        setError(description);
        notification.error({
          message: "Failed to load integration",
          description,
        });
        
        // Don't create mock config - let the user know there's an issue
        setConfig(null);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [id, reloadToken]);

  // Handle date filter changes and share them with components
  const handleDateFilterChange = (newFilter: DateFilterValue) => {
    console.log("IntegrationStudioPage - Date filter changed:", newFilter);
    // Set local state
    setDateFilter(newFilter);
    
    // This will trigger child components to refresh via props
    console.log("IntegrationStudioPage - Updated tab components with new date filter");
  };

  // Sync with dashboard filters when they change
  useEffect(() => {
    if (filterDates && filterDates.length === 2) {
      const newDateFilter = {
        preset: onPeriodSelected,
        startDate: filterDates[0].toISOString(),
        endDate: filterDates[1].toISOString(),
        displayText: onPeriodSelected
      };
      console.log("IntegrationStudioPage - Syncing with dashboard filters:", newDateFilter);
      setDateFilter(newDateFilter);
    }
  }, [filterDates, onPeriodSelected]);

  // Log when tab items are rendered with date filter
  useEffect(() => {
    if (dateFilter.startDate && dateFilter.endDate) {
      console.log("IntegrationStudioPage - Tabs will render with date filter:", {
        activeTab,
        dateFilter,
        configId: config?._id
      });
    }
  }, [dateFilter, activeTab, config]);

  // Handle tab change
  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
    
    // Debug log the transaction store when tabs change to help troubleshoot
    if (config && id) {
      const integrationId = id; // Use URL parameter directly
      console.group("IntegrationStudioPage - Tab Change Debug Info");
      console.log("Current tab:", newValue);
      console.log("Config:", {
        id: config._id,
        integrationId,
        name: config.name
      });
      console.log("Date filter active:", {
        hasFilter: Boolean(dateFilter?.startDate && dateFilter?.endDate),
        filter: dateFilter
      });
      
      if (transactions[integrationId]) {
        console.log("Transaction store data:", {
          transactionCount: transactions[integrationId].data.length,
          lastFetched: new Date(transactions[integrationId].lastFetched).toISOString(),
          dateParams: transactions[integrationId].fetchParams
        });
      } else {
        console.log("No data in transaction store for this connector");
      }
      console.groupEnd();
    }
  };

  // Debug: Log transaction store content when it changes
  useEffect(() => {
    if (config && id && process.env.NODE_ENV !== 'production') {
      const integrationId = id; // Use URL parameter directly
      if (transactions[integrationId]) {
        console.group("Transaction Store Content Update");
        console.log("Total transactions:", transactions[integrationId].data.length);
        
        if (transactions[integrationId].data.length > 0) {
          console.log("Sample transaction:", {
            id: transactions[integrationId].data[0].transactionId,
            status: transactions[integrationId].data[0].status,
            startDate: transactions[integrationId].data[0].startDate,
            hasPayload: Boolean(transactions[integrationId].data[0].payload)
          });
        } else {
          console.warn("No transactions found in store for integration:", integrationId);
          // If there are no transactions and we're in development, let components know they can use mock data
          const isDevelopment = import.meta.env.DEV || process.env.NODE_ENV === 'development';
          if (isDevelopment && window.sessionStorage) {
            console.log("Setting useMockData flag for development");
            window.sessionStorage.setItem('datacord_use_mock_data', 'true');
          }
        }
        
        console.log("Date range:", {
          startDate: transactions[integrationId].fetchParams?.startDate ? 
            new Date(transactions[integrationId].fetchParams.startDate).toISOString() : 'N/A',
          endDate: transactions[integrationId].fetchParams?.endDate ? 
            new Date(transactions[integrationId].fetchParams.endDate).toISOString() : 'N/A',
        });
        
        console.groupEnd();
      }
    }
  }, [transactions, config, id]);

  useEffect(() => {
    if (config && id) {
      const integrationId = id; // Use URL parameter directly
      
      // Check if we have any transaction data in sessionStorage from details page
      try {
        const storedData = sessionStorage.getItem(`datacord_transactions_${integrationId}`);
        if (storedData) {
          console.log("Found transaction data from details page in session storage");
          const parsedData = JSON.parse(storedData);
          
          if (Array.isArray(parsedData) && parsedData.length > 0) {
            console.log(`Syncing ${parsedData.length} transactions from details page`);
            // Use the syncTransactionsFromDetailsPage function from the store
            const syncTransactions = useTransactionsStore.getState().syncTransactionsFromDetailsPage;
            syncTransactions(integrationId, parsedData);
            
            // Clear the session storage to avoid using stale data next time
            sessionStorage.removeItem(`datacord_transactions_${integrationId}`);
          }
        }
      } catch (err) {
        console.error("Error syncing transactions from session storage:", err);
      }
    }
  }, [config, id]);

  // Function to fetch transactions with the correct parameters
  const loadTransactionsFromAnalytics = async () => {
    if (!config || !id) return;
    
    try {
      setLoadingAnalytics(true);
      const integrationId = id; // Use the URL parameter directly
      
      let startDate, endDate;
      if (dateFilter?.startDate && dateFilter?.endDate) {
        startDate = dateFilter.startDate;
        endDate = dateFilter.endDate;
      } else {
        endDate = new Date().toISOString();
        startDate = new Date(new Date().getTime() - (7 * 24 * 60 * 60 * 1000)).toISOString();
      }
      
      console.log("IntegrationStudioPage - Loading transactions from analytics endpoint:", {
        integrationId,
        startDate,
        endDate
      });
      
      // Use the analytical endpoint with a smaller page size for better performance
      await fetchTransactions(
        integrationId,
        1,
        100, // Reduced page size for better performance
        startDate,
        endDate,
        true // force refresh
      );
      
      // notification.success({
      //   message: "Transactions loaded successfully",
      //   description: `Data has been loaded from the analytics endpoint for ${integrationId}`,
      //   placement: "topRight"
      // });
    } catch (err) {
      console.error("Error loading transactions from analytics:", err);
      notification.error({
        message: "Failed to load transactions",
        description: err instanceof Error ? err.message : "Unknown error occurred",
        placement: "topRight"
      });
    } finally {
      setLoadingAnalytics(false);
    }
  };
  
  useEffect(() => {
    if (!config || loading || !id) return;
    const cached = transactions[id];
    const sameRange = (cached?.fetchParams?.startDate || null) === (dateFilter.startDate || null)
      && (cached?.fetchParams?.endDate || null) === (dateFilter.endDate || null);
    if (!cached?.data?.length || !sameRange) {
      loadTransactionsFromAnalytics();
    }
  }, [config, loading, id, dateFilter.startDate, dateFilter.endDate]);

  const sourceLabel = config?.source?.url || config?.source?.type || 'Source';
  const destinationLabel = config?.destination?.url || config?.destination?.type || 'Destination';
  const title = config?.integrationProfile?.integrationName || config?.integrationName || config?.name || 'Integration';
  const statusLabel = config?.status || 'Unknown';
  const statusTone = /active/i.test(statusLabel) ? 'is-active' : /pause/i.test(statusLabel) ? 'is-pause' : /error/i.test(statusLabel) ? 'is-error' : '';

  return (
    <div className={`studio-page${isDarkMode ? ' studio-page--dark' : ''}`}>
      <header className="studio-page__header">
        <div>
          <p className="studio-kicker">Integration studio</p>
          <h1>{title}</h1>
          <p className="studio-lede">
            <span title={sourceLabel}>{sourceLabel}</span>
            {' → '}
            <span title={destinationLabel}>{destinationLabel}</span>
          </p>
        </div>
        <div className="studio-heading-tools">
          <span className={`studio-badge ${statusTone}`}>{statusLabel}</span>
          <button type="button" className="studio-btn studio-btn--ghost" onClick={() => navigate(`/details/${id}`)}>
            Details
          </button>
          <button type="button" className="studio-btn studio-btn--ghost" onClick={loadTransactionsFromAnalytics} disabled={loadingAnalytics}>
            {loadingAnalytics ? 'Refreshing…' : 'Refresh runs'}
          </button>
        </div>
      </header>
        
      <section className="studio-board">
        {config ? (
          <div className="studio-toolbar">
            <div className="studio-segment" role="tablist" aria-label="Studio tools">
              {STUDIO_LABELS.map((label, index) => (
                <button
                  key={STUDIO_TABS[index]}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === index}
                  className={activeTab === index ? 'is-on' : ''}
                  onClick={() => handleTabChange(index)}
                >
                  {label}
                </button>
              ))}
            </div>
            <DateRangeFilter onFilterChange={handleDateFilterChange} loading={loading || loadingAnalytics} />
          </div>
        ) : null}

        {loading && !config ? (
          <div className="studio-loader">
            <Spin size="large" tip="Loading integration…" />
          </div>
        ) : error || !config ? (
          <div>
            <p className="studio-error">{error || 'This integration could not be loaded.'}</p>
            <button type="button" className="studio-btn" onClick={() => setReloadToken((value) => value + 1)}>Reload</button>
          </div>
        ) : (
          <ErrorBoundary>
            {runError ? <p className="studio-error">{runError}</p> : null}
            {activeTab === 0 && (
              <RecoveryDesk
                integrationId={id!}
                config={config}
                dateFilter={dateFilter}
                onStatusRefresh={() => setReloadToken((value) => value + 1)}
                onOpenInvestigator={() => handleTabChange(2)}
              />
            )}
            {activeTab === 1 && <div className="studio-tool"><IntegrationUniverse config={config} dateFilter={dateFilter} /></div>}
            {activeTab === 2 && <div className="studio-tool"><DataInvestigator configId={id!} dateFilter={dateFilter} /></div>}
            {activeTab === 3 && <div className="studio-tool"><DataReplayEnhanced configId={id!} dateFilter={dateFilter} /></div>}
            {activeTab === 4 && <div className="studio-tool"><PayloadRenderer configId={id!} /></div>}
            {activeTab === 5 && <div className="studio-tool"><ConfigEditor configId={config._id} config={config} onConfigUpdate={setConfig} /></div>}
          </ErrorBoundary>
        )}
      </section>
    </div>
  );
};

export default IntegrationStudioPage;